import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
// Served as a static application asset from public/ so it deploys with the app.
const deckAssetUrl = "/deck/ClutchCube-Investor-Deck-2026-c7f41a9e.pdf";

const submitSchema = z.object({
  name: z.string().trim().min(2).max(120),
  email: z.string().trim().email().max(200),
  phone: z.string().trim().min(6).max(40),
  company: z.string().trim().min(1).max(200),
  investor_type: z.string().min(1).max(80),
  estimated_interest: z.string().min(1).max(80),
  message: z.string().max(2000).optional().nullable(),
  website: z.string().optional().nullable(), // honeypot
  source: z.string().max(80).optional().nullable(),
  utm_source: z.string().max(120).optional().nullable(),
  utm_medium: z.string().max(120).optional().nullable(),
  utm_campaign: z.string().max(160).optional().nullable(),
  utm_content: z.string().max(160).optional().nullable(),
  referrer: z.string().max(500).optional().nullable(),
  landing_page: z.string().max(500).optional().nullable(),
});

async function sha256(input: string): Promise<string> {
  const enc = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest("SHA-256", enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function generateToken(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  // URL-safe base64
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

export const submitInvestorRequest = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => submitSchema.parse(data))
  .handler(async ({ data }) => {
    // Honeypot: silently accept, don't persist or email.
    if (data.website && data.website.trim().length > 0) {
      return { ok: true } as const;
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { investorLeadIdempotencyKey, providerIdempotencyKey } = await import(
      "./emails/idempotency"
    );

    // Deterministic idempotency: identical submissions (double-click, retry,
    // replay) collapse onto one lead row and one set of sends.
    const idempotencyKey = await investorLeadIdempotencyKey(data);

    const { data: existing } = await supabaseAdmin
      .from("investor_deck_requests")
      .select("id, email_sent, admin_notified")
      .eq("idempotency_key", idempotencyKey)
      .maybeSingle();
    if (existing) {
      return {
        ok: true,
        duplicate: true,
        emailSent: !!existing.email_sent,
        adminNotified: !!existing.admin_notified,
      } as const;
    }

    // Rate limit: 3 submissions per email per 10 minutes.
    const tenMinAgo = new Date(Date.now() - 10 * 60 * 1000).toISOString();
    const { count: recentCount } = await supabaseAdmin
      .from("investor_deck_requests")
      .select("id", { count: "exact", head: true })
      .eq("email", data.email.toLowerCase())
      .gte("created_at", tenMinAgo);
    if ((recentCount ?? 0) >= 3) {
      return { ok: false, error: "Too many requests. Please try again later." } as const;
    }

    const request = getRequest();
    const ip =
      request?.headers.get("x-forwarded-for")?.split(",")[0]?.trim() ||
      request?.headers.get("cf-connecting-ip") ||
      "";
    const ipHash = ip ? await sha256(ip) : null;

    const token = generateToken();

    const { data: inserted, error } = await supabaseAdmin
      .from("investor_deck_requests")
      .insert({
        name: data.name,
        email: data.email.toLowerCase(),
        phone: data.phone,
        company: data.company,
        investor_type: data.investor_type,
        estimated_interest: data.estimated_interest,
        message: data.message ?? null,
        deck_access_token: token,
        ip_hash: ipHash,
        idempotency_key: idempotencyKey,
        source: data.utm_source || data.source || "website",
        utm_source: data.utm_source ?? null,
        utm_medium: data.utm_medium ?? null,
        utm_campaign: data.utm_campaign ?? null,
        utm_content: data.utm_content ?? null,
        referrer: data.referrer ?? null,
        landing_page: data.landing_page ?? null,
      })
      .select("id, deck_access_token")
      .single();

    if (error || !inserted) {
      // Unique idempotency violation from a concurrent duplicate submission:
      // treat as the same logical request, never double-send.
      if (error?.code === "23505") {
        const { data: row } = await supabaseAdmin
          .from("investor_deck_requests")
          .select("email_sent, admin_notified")
          .eq("idempotency_key", idempotencyKey)
          .maybeSingle();
        return {
          ok: true,
          duplicate: true,
          emailSent: !!row?.email_sent,
          adminNotified: !!row?.admin_notified,
        } as const;
      }
      console.error("[investor] insert failed", error);
      return { ok: false, error: "Could not save request. Please try again." } as const;
    }

    // Atomic single-send claim. Only the first caller for this lead sends.
    const { data: claimed } = await supabaseAdmin.rpc("claim_investor_email_send", {
      _id: inserted.id,
    });
    if (!claimed) {
      return { ok: true, duplicate: true, emailSent: false, adminNotified: false } as const;
    }

    // Fire emails.
    const origin =
      request?.headers.get("origin") ||
      (request ? new URL(request.url).origin : "https://clutchcube.com");
    const deckUrl = `${origin}/investor-deck/${inserted.deck_access_token}`;

    const { sendEmail } = await import("./emails/send");
    const { investorWelcomeEmail, adminNotificationEmail, INVESTOR_REPLY_TO } = await import(
      "./emails/templates"
    );

    const investorTpl = investorWelcomeEmail({ name: data.name, deckUrl, baseUrl: origin });
    const investorRes = await sendEmail({
      to: data.email,
      subject: investorTpl.subject,
      html: investorTpl.html,
      text: investorTpl.text,
      reply_to: INVESTOR_REPLY_TO,
      category: "transactional_recipient",
      idempotencyKey: providerIdempotencyKey(inserted.id, "investor"),
    });

    const adminEmail = process.env.ADMIN_EMAIL;
    let adminRes: { ok: boolean; id?: string; error?: string } = {
      ok: false,
      error: "ADMIN_EMAIL not set",
    };
    if (adminEmail) {
      const adminTpl = adminNotificationEmail({
        name: data.name,
        email: data.email,
        phone: data.phone,
        company: data.company,
        investorType: data.investor_type,
        estimatedInterest: data.estimated_interest,
        message: data.message,
        source: data.utm_source || data.source || "website",
        leadId: inserted.id,
      });
      adminRes = await sendEmail({
        to: adminEmail,
        subject: adminTpl.subject,
        html: adminTpl.html,
        reply_to: data.email,
        category: "internal_notification",
        idempotencyKey: providerIdempotencyKey(inserted.id, "admin"),
      });
    } else {
      console.warn("[investor] ADMIN_EMAIL not configured");
    }

    const now = new Date().toISOString();
    await supabaseAdmin
      .from("investor_deck_requests")
      .update({
        email_sent: investorRes.ok,
        email_sent_at: investorRes.ok ? now : null,
        admin_notified: adminRes.ok,
        admin_notified_at: adminRes.ok ? now : null,
        resend_message_id: investorRes.id ?? null,
        admin_resend_message_id: adminRes.id ?? null,
        delivery_state: investorRes.ok ? "sent" : investorRes.suppressed ? "suppressed" : "failed",
        delivery_state_at: now,
        delivery_error: investorRes.ok ? null : (investorRes.error ?? null),
        status: investorRes.ok ? "deck_sent" : "new",
      })
      .eq("id", inserted.id);

    const { logInvestorActivity } = await import("./investor-activity.server");
    await logInvestorActivity({
      leadId: inserted.id,
      type: "lead_created",
      body: "Investor request received",
      metadata: { source: data.utm_source || data.source || "website" },
    });
    if (investorRes.ok) {
      await logInvestorActivity({
        leadId: inserted.id,
        type: "deck_sent",
        body: "Investor presentation email sent",
      });
    }

    return {
      ok: true,
      emailSent: investorRes.ok,
      adminNotified: adminRes.ok,
      suppressed: !!investorRes.suppressed,
    } as const;
  });

export const getInvestorDeckByToken = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => z.object({ token: z.string().min(10).max(200) }).parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // Atomic: locks the lead row, applies same-visit dedupe, increments views /
    // score, promotes the stage and logs one activity — concurrent calls from a
    // single visit cannot double-count.
    const { data: rows, error } = await supabaseAdmin.rpc("record_investor_deck_view", {
      _token: data.token,
    });

    const row = Array.isArray(rows) ? rows[0] : rows;
    if (error || !row) {
      return { ok: false as const, error: "invalid_token" as const };
    }

    return {
      ok: true as const,
      name: row.name,
      deckUrl: deckAssetUrl,

      filename: "ClutchCube-Investor-Deck-2026.pdf",
    };
  });
